import { useEffect, useRef, useState } from "react";

import { haptic } from "../lib/telegram";

interface Props {
  value: string;
  className?: string;
  ariaLabel: string;
  onSave: (value: string) => Promise<void>;
}

/**
 * Название, которое правится на месте.
 *
 * Отдельного экрана «переименовать» нет намеренно: переименование — это одно
 * слово, а не форма. Пустое значение не сохраняем — программа без имени
 * потеряется в списке; молча возвращаем прежнее.
 */
export default function EditableText({ value, className, ariaLabel, onSave }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => setDraft(value), [value]);

  useEffect(() => {
    if (editing) input.current?.focus();
  }, [editing]);

  async function commit() {
    setEditing(false);
    const next = draft.trim();
    if (next === "" || next === value) {
      setDraft(value);
      return;
    }
    try {
      await onSave(next);
    } catch {
      setDraft(value);
    }
  }

  if (!editing) {
    return (
      <button
        className={`editable ${className ?? ""}`}
        aria-label={`${ariaLabel}: изменить`}
        onClick={() => {
          haptic();
          setEditing(true);
        }}
      >
        {value}
        <span className="editable__pen" aria-hidden="true">
          ✎
        </span>
      </button>
    );
  }

  return (
    <input
      ref={input}
      className={`editable__input ${className ?? ""}`}
      aria-label={ariaLabel}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === "Enter") void commit();
        if (e.key === "Escape") {
          setDraft(value);
          setEditing(false);
        }
      }}
    />
  );
}
